/* eslint-disable */
import React, { useEffect } from "react";
import {
    HiXMark,
    HiOutlineUser,
    HiArrowRightOnRectangle,
    HiOutlineAdjustmentsHorizontal,
    HiOutlineChatBubbleLeftRight,
    HiOutlineQuestionMarkCircle,
} from "react-icons/hi2";
import { Link, useLocation, useNavigate } from "react-router-dom"; // For handling routing
import { FormattedMessage } from "react-intl";
import UserAvatar from "../../UserAvatar/UserAvatar";
import Typography from "@mui/material/Typography"; // Importing MUI Typography
import Notification from "../../Notifications/Notification";
import NotificationSkeleton from "../../Skeletons/NotificationSkeleton";
import { UserType } from "../../../types/user";
import { NotificationType } from "../../../types/notifications.t";
import { HiAdjustments, HiChatAlt2 } from "react-icons/hi";
import { useAuth0 } from "@auth0/auth0-react";
import { performLogout } from "../../../utils/auth";
import { useNetwork } from "../../NetworkProvider";

interface MobileProfileProps {
    isOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    user: UserType; // Add the user prop
    notifications?: NotificationType[]; // Make optional since we get from context
    newNotifications?: NotificationType[]; // Make optional since we get from context
    setIsNotificationsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const MobileProfile: React.FC<MobileProfileProps> = ({
    isOpen,
    setIsModalOpen,
    user,
    notifications: propNotifications,
    newNotifications: propNewNotifications,
    setIsNotificationsModalOpen,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { logout } = useAuth0();

    // Get notifications from NetworkProvider context
    const {
        notifications: contextNotifications,
        newNotifications: contextNewNotifications,
        notificationsLoading,
        notificationUsers,
        setNotifications,
    } = useNetwork();

    // Use prop notifications if provided, otherwise use context notifications
    const notifications = propNotifications || contextNotifications || [];
    const newNotifications =
        propNewNotifications || contextNewNotifications || [];

    // Update URL when modal opens
    useEffect(() => {
        if (isOpen) {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set("settings", "true");
            navigate(`${location.pathname}?${searchParams.toString()}`, {
                replace: true, // Use replace to avoid adding to history stack
            });
        }
    }, [isOpen, location.pathname, location.search, navigate]);

    const handleSeeMoreNotifications = () => {
        const params = new URLSearchParams(location.search);
        params.delete("settings"); // Remove settings parameter
        params.set("notifications", "true");
        navigate(`${location.pathname}?${params.toString()}`);
        setIsNotificationsModalOpen(true);
        setIsModalOpen(false);
    };

    const handleClickNotification = (notification: NotificationType) => {
        // Don't add any URL parameters when clicking from mobile profile
        navigate(notification.link);
        setIsModalOpen(false);
    };
    // Handle modal close
    const handleClose = () => {
        const searchParams = new URLSearchParams(location.search);
        const hasNotifications = searchParams.get("notifications") === "true";
        searchParams.delete("settings");

        // If notifications were open, keep them open
        if (hasNotifications) {
            searchParams.set("notifications", "true");
        }

        navigate(`${location.pathname}?${searchParams.toString()}`, {
            replace: true,
        });
        setIsModalOpen(false);
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/70 z-[2000] flex justify-start items-start overflow-y-auto no-scrollbar bg-white"
            style={{
                zIndex: 2000,
                height: "100vh",
            }}
        >
            <div
                className="px-6 bg-white w-full min-h-screen flex flex-col items-center gap-5 pb-40 overflow-y-auto no-scrollbar"
                onClick={(e) => e.stopPropagation()} // Prevents closing when clicking inside the modal
            >
                <button
                    onClick={handleClose}
                    style={{
                        position: "absolute",
                        top: "20px",
                        left: "20px",
                    }}
                    className="text-xl"
                >
                    <HiXMark />
                </button>
                <div className="flex flex-col items-center justify-center gap-5">
                    <div style={{ marginTop: "3rem" }}>
                        <UserAvatar user={user} />
                    </div>
                    <div>
                        <Typography
                            variant="h3"
                            component="div"
                            sx={{ fontWeight: 700, fontSize: "1.47rem" }}
                        >
                            {user?.first_name} {user?.last_name}
                        </Typography>
                    </div>
                </div>

                <div className="flex flex-col mt-2 w-full flex-grow">
                    <div className="flex flex-col items-start justify-start w-full gap-2">
                        <h3 className="font-bold text-left text-lg">
                            <FormattedMessage id="mobile.notifications" />
                        </h3>

                        {notificationsLoading ? (
                            <div className="flex flex-col items-start justify-start w-full">
                                {Array.from({ length: 3 }, (_, index) => (
                                    <NotificationSkeleton
                                        key={index}
                                        mobile={true}
                                    />
                                ))}
                            </div>
                        ) : notifications.length > 0 ? (
                            <div className="flex flex-col items-start justify-start w-full">
                                {notifications
                                    .slice(0, 3)
                                    .map((notification) => {
                                        return (
                                            <div
                                                key={
                                                    notification.notification_id
                                                }
                                            >
                                                <Notification
                                                    key={
                                                        notification.notification_id
                                                    }
                                                    notification={notification}
                                                    notifications={
                                                        notifications
                                                    }
                                                    setNotifications={
                                                        setNotifications
                                                    }
                                                    onClick={
                                                        handleClickNotification
                                                    }
                                                    onClose={() =>
                                                        setIsModalOpen(false)
                                                    }
                                                    notificationUsers={
                                                        notificationUsers
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                            </div>
                        ) : (
                            <div className="flex gap-1 justify-between py-3">
                                <FormattedMessage id="mobile.noNotifications" />
                            </div>
                        )}
                    </div>
                    {!notificationsLoading && notifications.length > 3 && (
                        <button
                            className="font-bold text-md mt-2"
                            onClick={handleSeeMoreNotifications}
                        >
                            <FormattedMessage id="mobile.seeMore" />
                        </button>
                    )}
                </div>

                {/* White component below the name */}
                <div className="flex flex-col items-start justify-start w-full gap-1">
                    <h3 className="font-bold text-left text-lg pb-2">
                        Mon compte
                    </h3>
                    <ul className="list-none p-0 font-extrabold w-full">
                        {/* Example of a link with an icon */}
                        <li className="flex items-center gap-3 py-1 mb-3  cursor-pointer">
                            <HiOutlineUser className="mr-2.5" />
                            <Link
                                to="/settings"
                                className="no-underline text-black"
                            >
                                <FormattedMessage id="mobile.personalInformation" />
                            </Link>
                        </li>
                        <li className="flex items-center gap-3 py-1 mb-3 cursor-pointer text-gray-500">
                            <HiOutlineAdjustmentsHorizontal className="mr-2.5" />
                            <FormattedMessage id="mobile.general" />
                        </li>
                        <li className="flex items-center gap-3 py-1 mb-3  cursor-pointer text-gray-500">
                            <HiOutlineChatBubbleLeftRight className="mr-2.5" />
                            <FormattedMessage id="mobile.help" />
                        </li>
                        <li className="flex items-center gap-3 py-1 mb-3 cursor-pointer text-gray-500">
                            <HiOutlineQuestionMarkCircle className="mr-2.5" />
                            <FormattedMessage id="mobile.faq" />
                        </li>
                    </ul>
                </div>
                <button
                    className="font-semibold flex items-start gap-3 w-full"
                    style={{
                        color: "#ff3838",
                    }}
                    onClick={() => performLogout(logout)}
                >
                    <HiArrowRightOnRectangle className="text-xl" />
                    <FormattedMessage id="mobile.logout" />
                </button>
            </div>
        </div>
    );
};

export default MobileProfile;
